/**
 * Versements au guichet et situation des inscriptions (données du serveur).
 *
 * Un reçu règle une ou plusieurs lignes de la fiche de l'élève (inscription, articles, tranches) ;
 * il ne se supprime pas : il s'annule avec un motif, et ses montants sortent des totaux.
 */
import { http, isOfflineError, orUndefined } from "./http";
import {
  enqueue,
  newActionKey,
  provisionalNote,
  provisionalRef,
  registerProcessor,
  replayOptions,
  type QueuedOffline,
} from "@/offline/outbox";
import type { ReceiptData } from "@/utils/receipt";
import { formatMoney, todayISO } from "@/utils/format";
import type { EnrollmentBalance, FeeKind, FeeLine, PaymentMethod, Receipt } from "@/types";

/* -------------------------------------------------------------------------- */
/* Format du backend                                                           */
/* -------------------------------------------------------------------------- */

export interface ApiBalance {
  enrollmentId: number;
  studentId: number;
  classId: number;
  cycleId: number;
  schoolYear: string;
  isReenrollment: boolean;
  lines: Array<{
    feeItemId: number;
    kind: FeeKind;
    label: string;
    dueDate: string | null;
    totalDue: number;
    totalPaid: number;
    remaining: number;
    settlement: FeeLine["settlement"];
    applicable: boolean;
  }>;
  totalDue: number;
  totalPaid: number;
  remaining: number;
}

interface ApiReceipt {
  id: number;
  receiptNo: string;
  enrollmentId: number;
  studentId: number;
  schoolYear: string;
  date: string;
  method: PaymentMethod;
  note: string | null;
  total: number;
  recordedByName: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  cancelledByName: string | null;
  lines: Array<{ id: number; feeItemId: number; label: string; amountPaid: number; status: "TOTAL" | "PARTIEL" }>;
}

/** Libellé affiché de chaque mode de règlement. */
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  ESPECES: "Espèces",
  MOBILE_MONEY: "Mobile money",
  VIREMENT: "Virement bancaire",
  CHEQUE: "Chèque",
};

export function toBalance(b: ApiBalance): EnrollmentBalance {
  const enrollmentId = String(b.enrollmentId);
  return {
    enrollmentId,
    studentId: String(b.studentId),
    classId: String(b.classId),
    cycleId: String(b.cycleId),
    schoolYear: b.schoolYear,
    isReenrollment: b.isReenrollment,
    lines: b.lines.map((l) => ({
      key: String(l.feeItemId),
      enrollmentId,
      kind: l.kind,
      label: l.label,
      dueDate: l.dueDate ?? "",
      due: l.totalDue,
      paid: l.totalPaid,
      remaining: l.remaining,
      settlement: l.settlement,
      applicable: l.applicable,
    })),
    totalDue: b.totalDue,
    totalPaid: b.totalPaid,
    remaining: b.remaining,
  };
}

function toReceipt(r: ApiReceipt): Receipt {
  return {
    id: String(r.id),
    receiptNo: r.receiptNo,
    enrollmentId: String(r.enrollmentId),
    studentId: String(r.studentId),
    schoolYear: r.schoolYear,
    date: r.date,
    method: r.method,
    note: r.note ?? "",
    total: r.total,
    recordedBy: r.recordedByName,
    cancelledAt: r.cancelledAt,
    cancelReason: r.cancelReason ?? "",
    cancelledBy: r.cancelledByName ?? "",
    lines: r.lines.map((l) => ({
      id: String(l.id),
      feeKey: String(l.feeItemId),
      label: l.label,
      amount: l.amountPaid,
      status: l.status,
    })),
  };
}

/* -------------------------------------------------------------------------- */
/* Situation                                                                   */
/* -------------------------------------------------------------------------- */

/** Situation de toutes les inscriptions (non annulées) d'une année. */
export async function getBalances(schoolYear?: string): Promise<EnrollmentBalance[]> {
  return (await http.get<ApiBalance[]>("/balances", { schoolYear })).map(toBalance);
}

/** Situation de plusieurs années à la fois (liste des années connues). */
export async function getBalancesForYears(years: string[]): Promise<EnrollmentBalance[]> {
  return (await Promise.all(years.map((y) => getBalances(y)))).flat();
}

export async function getEnrollmentBalance(enrollmentId: string): Promise<EnrollmentBalance> {
  return toBalance(await http.get<ApiBalance>(`/enrollments/${enrollmentId}/balance`));
}

/* -------------------------------------------------------------------------- */
/* Reçus                                                                       */
/* -------------------------------------------------------------------------- */

export interface ReceiptInput {
  date?: string;
  method: PaymentMethod;
  note?: string;
  lines: Array<{ feeKey: string; amount: number }>;
}

/** Corps d'un versement pour le serveur (aussi utilisé par l'inscription). */
export function receiptBody(input: ReceiptInput) {
  return {
    date: orUndefined(input.date),
    method: input.method,
    note: orUndefined(input.note),
    lines: input.lines.filter((l) => l.amount > 0).map((l) => ({ feeItemId: Number(l.feeKey), amount: l.amount })),
  };
}

/** Tous les reçus de l'école (annulés compris), les plus récents en premier. */
export async function getReceipts(): Promise<Receipt[]> {
  return (await http.getAll<ApiReceipt>("/receipts")).map(toReceipt);
}

/**
 * Encaisse un versement ; renvoie le reçu et la situation de l'inscription après lui.
 * Hors ligne (avec `offline`) : le paiement est noté sur l'appareil et envoyé au retour du réseau. Il renvoie
 * alors un reçu provisoire à remettre au parent ; le reçu officiel numéroté viendra du serveur.
 */
export async function recordReceipt(
  enrollmentId: string,
  input: ReceiptInput,
  offline?: { label: string; provisional: (ref: string) => ReceiptData },
): Promise<{ receipt: Receipt; balance: EnrollmentBalance } | QueuedOffline> {
  // La date du paiement est celle du guichet, pas celle d'un envoi plus tard.
  const body = { enrollmentId: Number(enrollmentId), ...receiptBody(input), date: input.date || todayISO() };
  const key = newActionKey();
  try {
    const res = await http.post<{ receipt: ApiReceipt; balance: ApiBalance }>("/receipts", body, { idempotencyKey: key });
    return { receipt: toReceipt(res.receipt), balance: toBalance(res.balance) };
  } catch (error) {
    if (!offline || !isOfflineError(error)) throw error;
    const ref = provisionalRef(key);
    const provisional = offline.provisional(ref);
    await enqueue({
      id: key,
      kind: "receipt",
      label: offline.label,
      payload: { ...body, note: provisionalNote(input.note, ref) },
      provisional,
    });
    return { queued: true, provisional };
  }
}

registerProcessor("receipt", async (item) => {
  await http.post("/receipts", item.payload, replayOptions(item));
  return { done: true };
});

/** Annule un reçu (motif obligatoire). */
export async function cancelReceipt(id: string, reason: string): Promise<Receipt> {
  const res = await http.post<{ receipt: ApiReceipt }>(`/receipts/${id}/cancel`, { reason: reason.trim() });
  return toReceipt(res.receipt);
}

/* -------------------------------------------------------------------------- */
/* Répartition d'un versement sur les frais cochés                             */
/* -------------------------------------------------------------------------- */

export interface CheckedAllocation {
  /** Montant versé sur chaque frais (clé de ligne). */
  amounts: Record<string, number>;
  /** Frais non cochés qui reçoivent le surplus (cochés d'office, signalés « surplus »). */
  overflowKeys: string[];
  /** Montant au-delà de TOUT le reste à payer : le versement doit être refusé tant qu'il est positif. */
  excess: number;
  /** Total encore dû sur toutes les lignes. */
  totalRemaining: number;
}

/**
 * Répartit un versement : d'abord sur les frais cochés (dans l'ordre de la fiche), chacun au plus son reste
 * dû ; le surplus éventuel va sur les frais qui suivent (la tranche suivante), puis sur ceux d'avant ; ce qui dépasse
 * tout le reste à payer est signalé dans `excess` (versement refusé).
 */
export function allocateChecked(lines: FeeLine[], amount: number, checked: ReadonlySet<string>): CheckedAllocation {
  const payable = lines.filter((l) => l.remaining > 0);
  const totalRemaining = payable.reduce((s, l) => s + l.remaining, 0);
  let left = Math.max(0, Math.round(amount));
  const amounts: Record<string, number> = {};
  const overflowKeys: string[] = [];
  for (const line of payable.filter((l) => checked.has(l.key))) {
    const part = Math.min(left, line.remaining);
    if (part > 0) amounts[line.key] = part;
    left -= part;
  }
  // Surplus : d'abord les frais qui suivent le dernier frais coché (la tranche suivante), puis ceux d'avant.
  const lastChecked = payable.reduce((last, l, i) => (checked.has(l.key) ? i : last), -1);
  const order = [...payable.slice(lastChecked + 1), ...payable.slice(0, lastChecked + 1)];
  for (const line of order.filter((l) => !checked.has(l.key))) {
    if (left <= 0) break;
    const part = Math.min(left, line.remaining);
    amounts[line.key] = part;
    overflowKeys.push(line.key);
    left -= part;
  }
  return { amounts, overflowKeys, excess: left, totalRemaining };
}

/** Message d'erreur d'un versement à refuser, ou null s'il peut être enregistré. */
export function allocationError(allocation: CheckedAllocation, amount: number, checkedCount: number): string | null {
  if (amount > 0 && checkedCount === 0) return "Cochez au moins un frais que ce versement règle.";
  if (allocation.excess > 0) {
    return `Le montant (${formatMoney(amount)}) dépasse le reste à payer de l'élève (${formatMoney(allocation.totalRemaining)}).`;
  }
  return null;
}
